import { createElement, isValidElement } from "react";
import ToastRailContent from "../components/common/ToastRailContent";

/**
 * Native rail timing policy. The controller receives ToastItem values from
 * react-toastify, so it deliberately knows nothing about Layout or ordinary
 * toast call sites.
 */
export const TOAST_RAIL_LIFETIMES = Object.freeze({
  success: 2000,
  info: 3000,
  warn: 5000,
  warning: 5000,
  error: 6000,
});

const DEFAULT_AUTO_CLOSE = TOAST_RAIL_LIFETIMES.info;

// The rail is ONE bottom-center surface. React-Toastify builds a separate
// container element per active per-toast `position`, and the container
// className — with all the rail chrome — goes to every one of them, so a
// producer's own `position` ("bottom-right" on showUndoToast) would render a
// rail capsule pinned to the library's default corner: under the recording
// bar, outside the safe area, and exempt from the modal zero-offset. Every
// decorating update therefore pulls its toast onto the rail's own container.
// Must match `position` in toastContainerConfig.js — kept as a literal here so
// this module stays free of a react-toastify import; the agreement is pinned
// by toastRailController.test.js and toastRailContract.test.js.
export const TOAST_RAIL_POSITION = "bottom-center";

function severityFor(item) {
  const type = typeof item === "object" && item ? item.type : undefined;
  if (type === "warn") return "warning";
  return type;
}

/**
 * Returns the controller-owned lifetime for a ToastItem. A bare string has no
 * severity information and intentionally uses the info fallback.
 */
export function getToastRailAutoClose(item) {
  return TOAST_RAIL_LIFETIMES[severityFor(item)] || DEFAULT_AUTO_CLOSE;
}

function emptySnapshot(expanded = false, paused = false) {
  return Object.freeze({
    activeCount: 0,
    visibleId: null,
    repeatCount: 1,
    canDismiss: true,
    action: null,
    toasts: new Map(),
    expanded,
    paused,
  });
}

/**
 * The slice of a record a single capsule renders. Every live toast gets one, so
 * a capsule can render its OWN repeat count, action and dismissibility instead
 * of the newest toast's. The top-level fields stay as the visible capsule's
 * summary.
 */
function toastStateFor(record) {
  return Object.freeze({
    id: record.id,
    repeatCount: record.repeatCount,
    canDismiss: record.canDismiss,
    action: record.action,
  });
}

function snapshotFor(active, expanded, paused) {
  let visible = null;
  // A Map, not an object: toast ids are strings or numbers, and object keys
  // would coerce 1 and "1" into the same capsule.
  const toasts = new Map();
  active.forEach((record) => {
    visible = record;
    toasts.set(record.id, toastStateFor(record));
  });

  if (!visible) return emptySnapshot(expanded, paused);

  return Object.freeze({
    activeCount: active.size,
    visibleId: visible.id,
    repeatCount: visible.repeatCount,
    canDismiss: visible.canDismiss,
    action: visible.action,
    toasts,
    expanded,
    paused,
  });
}

function railMetadata(item) {
  return (item && item.data && item.data.toastRail) || {};
}

// Rail-authored content: the adapter the controller wraps toasts in, or a body
// that opts out because it renders the rail structure itself.
function isRailAdapterContent(content) {
  if (!isValidElement(content)) return false;
  return content.type === ToastRailContent || content.type?.toastRailSelfContained === true;
}

function isExplicit(record) {
  return record.explicitToastId === true;
}

/**
 * Creates a small external store around react-toastify's onChange stream.
 * `toastApi` is injected to keep this module free of a react-toastify import
 * and to make its lifecycle behavior directly testable. `renderContent` is the
 * optional seam that turns an arriving ToastItem into the rail's toast content;
 * omitting it leaves the controller a pure counting/timing store.
 */
export function createToastRailController({ toastApi, renderContent } = {}) {
  const api = toastApi || {};
  const active = new Map();
  const idsBySeverityAndContent = new Map();
  const listeners = new Set();
  const selfUpdatedIds = new Set();
  let expanded = false;
  let paused = false;
  let destroyed = false;
  let snapshot = emptySnapshot();

  function publish() {
    snapshot = snapshotFor(active, expanded, paused);
    listeners.forEach((listener) => listener());
  }

  function duplicateIdFor(severity, content) {
    const idsByContent = idsBySeverityAndContent.get(severity);
    return idsByContent && idsByContent.get(content);
  }

  function index(record) {
    if (!record.isString) return;
    let idsByContent = idsBySeverityAndContent.get(record.severity);
    if (!idsByContent) {
      idsByContent = new Map();
      idsBySeverityAndContent.set(record.severity, idsByContent);
    }
    idsByContent.set(record.content, record.id);
  }

  function unindex(record) {
    if (!record || !record.isString) return;
    const idsByContent = idsBySeverityAndContent.get(record.severity);
    if (!idsByContent || idsByContent.get(record.content) !== record.id) return;

    idsByContent.delete(record.content);
    if (idsByContent.size === 0) idsBySeverityAndContent.delete(record.severity);
  }

  function remove(id) {
    const record = active.get(id);
    if (!record) return false;
    active.delete(id);
    unindex(record);
    selfUpdatedIds.delete(id);
    return true;
  }

  function dismiss(id) {
    if (typeof api.dismiss === "function") api.dismiss(id);
  }

  /**
   * One `toast.update` per arrival carries both rail concerns: the severity
   * lifetime (only when the producer did not fix its own) and the rail content
   * adapter. Issuing them as two updates would rebuild the toast twice, replay
   * the entry animation twice, and restart its timer twice.
   */
  function decorate(item, metadata) {
    if (typeof api.update !== "function") return;

    const options = {};

    if (typeof item.content === "string" && metadata.preserveAutoClose !== true) {
      options.autoClose = getToastRailAutoClose(item);
    }

    if (typeof renderContent === "function") {
      const rendered = renderContent(item);
      if (rendered !== undefined) options.render = rendered;
    }

    if (Object.keys(options).length === 0) return;

    options.position = TOAST_RAIL_POSITION;

    selfUpdatedIds.add(item.id);
    api.update(item.id, options);
  }

  /**
   * A producer's `toast.update` can change what a tracked toast SAYS. The
   * dedupe index and the per-toast state must follow the content on screen,
   * not the content at arrival: a stale index treats the next genuinely-new
   * toast that says what this one USED to say as a repeat — and silently
   * dismisses it. The repeat badge resets with the text for the same reason:
   * ×N must never count a message that is no longer showing.
   *
   * Rail-authored content is exempt. The controller's own wrap replaces
   * content with the adapter element, so an option-only producer update (a
   * progress tick, a retime) reports that adapter back as the current
   * content; re-keying on it would swap the producer's string for rail
   * chrome, breaking dedupe and flipping dismissibility.
   */
  function rekey(item) {
    const record = active.get(item.id);
    if (!record || isRailAdapterContent(item.content)) return;

    const severity = severityFor(item);
    const isString = typeof item.content === "string";
    const contentChanged = record.content !== item.content;
    if (!contentChanged && record.severity === severity && record.isString === isString) return;

    unindex(record);
    record.content = item.content;
    record.severity = severity;
    record.isString = isString;
    // Another live toast can already own the (severity, content) slot this
    // record just moved onto. Indexing over it would strand that toast
    // unindexed for the rest of its life — its later duplicates would escape
    // dedupe entirely. The incumbent keeps the slot; this record simply stops
    // participating in dedupe under its new text.
    const incumbentId = record.isString ? duplicateIdFor(record.severity, record.content) : undefined;
    if (incumbentId === undefined || incumbentId === record.id) index(record);

    // canDismiss and action both re-derive by the arrival rules: explicit
    // metadata wins for SETTING (an update that restates nothing keeps the
    // incumbent action rather than clearing it), and the content-type default
    // follows the content — a
    // string that becomes an undo-style custom body must not stay
    // rail-swipeable (GAP-01's harm class outranks the one cost, which is
    // that a swipe in flight at that exact moment is dropped and has to be
    // repeated). Metadata-ONLY updates (content and severity unchanged) are
    // out of scope by the early return above; no live producer issues one.
    const metadata = railMetadata(item);
    const canDismiss = metadata.canDismiss === undefined ? isString : metadata.canDismiss !== false;
    const action = metadata.action == null ? record.action : metadata.action;
    const repeatCount = contentChanged ? 1 : record.repeatCount;
    const visibleStateChanged =
      record.canDismiss !== canDismiss || record.repeatCount !== repeatCount || record.action !== action;
    record.canDismiss = canDismiss;
    record.action = action;
    record.repeatCount = repeatCount;
    if (visibleStateChanged) publish();
  }

  /**
   * A producer's own `toast.update` can replace a live toast's `render` outright
   * (`usePostMessages` doneToast/updateToast, the `useChats` recording toasts),
   * which strips the rail treatment for the rest of that toast's life. Re-apply it.
   *
   * Only the content is re-applied, never the timing: the producer's update carries
   * its own `autoClose` in every one of those call sites, and the lifetime decision
   * was already made when the toast arrived.
   */
  function redecorate(item) {
    if (!active.has(item.id)) return;
    if (typeof api.update !== "function" || typeof renderContent !== "function") return;

    const rendered = renderContent(item);
    // `undefined` means the content is already the rail adapter — an update that
    // did not touch `render` (a progress tick) reports it as the current content,
    // and re-wrapping would nest the adapter inside itself.
    if (rendered === undefined) return;

    selfUpdatedIds.add(item.id);
    api.update(item.id, { render: rendered, position: TOAST_RAIL_POSITION });
  }

  function add(item) {
    if (active.has(item.id)) return;

    const metadata = railMetadata(item);
    const isString = typeof item.content === "string";
    const record = {
      id: item.id,
      content: item.content,
      severity: severityFor(item),
      isString,
      explicitToastId: metadata.explicitToastId === true,
      // A custom (non-string) body is an app-authored component with its own
      // affordances — `showUndoToast` renders an Undo button that cancels a
      // deferred archive, so a stray swipe would commit the very action the
      // toast was offering to reverse. Those producers do say so, with
      // `draggable: false`, but v11's ToastItem does not expose original options
      // to `toast.onChange`, so the controller cannot read that choice. Content
      // type is the one producer signal it CAN see, and it already partitions
      // retiming and dedupe, so it partitions the velocity path too: a custom
      // body is not rail-dismissible unless its metadata opts in. Explicit
      // `data.toastRail.canDismiss` still wins in BOTH directions.
      canDismiss: metadata.canDismiss === undefined ? isString : metadata.canDismiss !== false,
      action: metadata.action == null ? null : metadata.action,
      repeatCount: 1,
    };
    const survivorId = record.isString ? duplicateIdFor(record.severity, record.content) : undefined;
    const survivor = survivorId === undefined ? undefined : active.get(survivorId);

    if (survivor) {
      if (record.explicitToastId && !isExplicit(survivor)) {
        // Remove before dismissing so a synchronous removed echo cannot clear
        // the new explicit survivor's duplicate index.
        remove(survivor.id);
        record.repeatCount = survivor.repeatCount + 1;
        active.set(record.id, record);
        index(record);
        decorate(item, metadata);
        dismiss(survivor.id);
        publish();
        return;
      }

      survivor.repeatCount += 1;
      dismiss(record.id);
      publish();
      return;
    }

    active.set(record.id, record);
    index(record);
    decorate(item, metadata);
    publish();
  }

  function onChange(item) {
    if (destroyed || !item) return;

    if (item.status === "added") {
      add(item);
      return;
    }

    if (item.status === "updated") {
      // `toast.update` emits this event. The controller never uses an update
      // event as a new arrival, avoiding update/dismiss feedback loops.
      //
      // Re-key BEFORE the echo check: rekey is a no-op for rail-authored
      // (adapter) content, so an echo cannot corrupt the index — and a
      // producer update dispatched while a rail echo is still in flight must
      // not be mistaken for that echo. v11 defers every update by delay: 100,
      // so an update ISSUED before the rail's can DISPATCH after it; arrival
      // order cannot tell the two apart. Content can: every rail-issued
      // update renders the adapter, and no producer ever sends one. A swallow
      // therefore requires BOTH the armed flag and adapter content, which is
      // also what stops re-decoration from looping: the rail's own update
      // produces exactly one adapter-content echo, and that echo goes no
      // further.
      rekey(item);
      const wasSelfUpdate = selfUpdatedIds.delete(item.id);
      if (wasSelfUpdate && isRailAdapterContent(item.content)) return;
      redecorate(item);
      return;
    }

    if (item.status === "removed" && remove(item.id)) publish();
  }

  function subscribe(listener) {
    if (destroyed || typeof listener !== "function") return () => {};
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  function getSnapshot() {
    return snapshot;
  }

  function setExpanded(nextExpanded) {
    if (destroyed || expanded === Boolean(nextExpanded)) return;
    expanded = Boolean(nextExpanded);
    publish();
  }

  function setPaused(nextPaused) {
    if (destroyed || paused === Boolean(nextPaused)) return;
    paused = Boolean(nextPaused);
    publish();
  }

  function teardown() {
    if (destroyed) return;
    destroyed = true;
    const stateChanged = active.size > 0 || expanded || paused;
    active.clear();
    idsBySeverityAndContent.clear();
    selfUpdatedIds.clear();
    expanded = false;
    paused = false;
    listeners.clear();
    if (stateChanged) snapshot = emptySnapshot();
  }

  return {
    onChange,
    subscribe,
    getSnapshot,
    setExpanded,
    setPaused,
    teardown,
  };
}

/**
 * Composes the whole native rail lifecycle behind one call so `Layout` owns no
 * rail logic beyond "attach while native, detach on unmount".
 *
 * Returns `null` outside the native app: no controller is built and nothing
 * subscribes, so a desktop or phone-browser session behaves exactly as it does
 * on `stage`. Inside the native app it builds exactly one controller, registers
 * exactly one `toast.onChange` listener, and hands back a `detach` that both
 * unsubscribes and tears the controller down, once.
 *
 * `ToastRailContent` is passed as the DIRECT toast content on purpose.
 * React-Toastify v11 injects `closeToast` / `toastProps` with `cloneElement`,
 * and only when the content element's type is not a string — so wrapping the
 * adapter in a DOM element would leave its already-closing guard inert with no
 * crash and no warning.
 */
export function attachToastRailLifecycle({ isMobileApp, toastApi, createController } = {}) {
  if (!isMobileApp) return null;

  const api = toastApi || {};
  const factory = typeof createController === "function" ? createController : createToastRailController;

  let controller = null;
  // `controller` is read at call time, so the adapter shares the very controller
  // that produced the arrival it is rendering for. Returns `undefined` when the
  // content already IS the adapter, which is how re-decoration avoids nesting the
  // adapter inside itself.
  //
  // `toastId` is what lets a capsule find itself in the shared snapshot. Without
  // it every capsule would render the newest toast's count, action and
  // dismissibility.
  //
  // `toastRailSelfContained` is an opt-out for a body that already renders the
  // rail's content structure itself (RecordingProgressToast). It opts out of the
  // WHOLE adapter, not just the decorating update: such a body gets no `+N`
  // overflow badge, no repeat count, no rail velocity-swipe and no
  // `data-expanded` / `data-paused`. Set it only on a body that genuinely wants
  // all of that, and keep the flag on the component type — an instance-level
  // prop would not survive the ToastItem round-trip.
  const renderContent = (item) => {
    if (isRailAdapterContent(item.content)) return undefined;
    return createElement(ToastRailContent, { content: item.content, controller, toastId: item.id });
  };

  controller = factory({ toastApi: api, renderContent });

  const unsubscribe = typeof api.onChange === "function" ? api.onChange(controller.onChange) : null;
  let detached = false;

  return {
    controller,
    renderContent,
    detach() {
      if (detached) return;
      detached = true;
      if (typeof unsubscribe === "function") unsubscribe();
      if (typeof controller.teardown === "function") controller.teardown();
    },
  };
}
